import { API_CONFIG } from '../config/apiConfig';

export interface ImageOptimizeOptions {
  width?: number; // Target width in pixels (default: 800)
  height?: number; // Target height in pixels
  quality?: number; // Compression quality 1-100 (default: 82)
  format?: 'webp' | 'jpg' | 'png'; // Output format (default: 'webp')
  original?: boolean; // If true, returns raw uncompressed URL
}

/**
 * Resolves an image path or URL into a fully-qualified, CDN-optimized WebP URL.
 * Automatically transforms heavy iPhone camera photos (5-6MB) into lightweight (~70KB)
 * sharp WebP images delivered via Cloudflare edge CDN (wsrv.nl), with zero quality loss
 * to the human eye and zero modifications to stored original files.
 */
export function resolveImageUrl(pathOrUrl?: string | null, options?: ImageOptimizeOptions | any): string {
  if (!pathOrUrl || typeof pathOrUrl !== 'string') return '';

  const opt: ImageOptimizeOptions = (options && typeof options === 'object' && !Array.isArray(options)) ? options : {};

  let absoluteUrl = '';

  // Handle absolute URL (http/https/data)
  if (/^(https?:)?\/\//i.test(pathOrUrl) || /^data:/i.test(pathOrUrl)) {
    try {
      absoluteUrl = encodeURI(pathOrUrl);
    } catch {
      absoluteUrl = pathOrUrl;
    }
  } else if (pathOrUrl.startsWith('/')) {
    // Relative path with leading slash
    try {
      absoluteUrl = encodeURI(`${API_CONFIG.BASE_URL}${pathOrUrl}`);
    } catch {
      absoluteUrl = `${API_CONFIG.BASE_URL}${pathOrUrl}`;
    }
  } else {
    // Relative path without leading slash
    try {
      absoluteUrl = encodeURI(`${API_CONFIG.BASE_URL}/${pathOrUrl}`);
    } catch {
      absoluteUrl = `${API_CONFIG.BASE_URL}/${pathOrUrl}`;
    }
  }

  // Skip optimization if caller requested raw original or for data/blob/svg/local URLs
  if (
    opt.original ||
    !absoluteUrl ||
    absoluteUrl.startsWith('data:') ||
    absoluteUrl.startsWith('blob:') ||
    absoluteUrl.toLowerCase().endsWith('.svg') ||
    absoluteUrl.includes('localhost') ||
    absoluteUrl.includes('127.0.0.1')
  ) {
    return absoluteUrl;
  }

  // If already proxied via wsrv.nl, unwrap underlying URL
  if (absoluteUrl.includes('wsrv.nl/')) {
    try {
      const parsed = new URL(absoluteUrl);
      const underlying = parsed.searchParams.get('url');
      if (underlying) {
        return underlying;
      }
    } catch {
      // ignore
    }
  }

  // Files in Supabase are already optimized to ~120KB and served directly via global CDN.
  // Delivering them directly avoids high latency (20s+ cold starts) or timeouts from external Dutch proxies.
  return absoluteUrl;
}

/**
 * Resolves the raw, original full-resolution image URL without any CDN resizing or compression.
 */
export function resolveOriginalImageUrl(pathOrUrl?: string | null): string {
  return resolveImageUrl(pathOrUrl, { original: true });
}
